// Catalogue area shell (office). Renders the catalogue section nav and an
// <Outlet/> for the S1–S8 screens. Registered in the router at /catalogue; the
// index redirects to /catalogue/products.
//
// UX-2: the nav is the §3.10 `RouterTabs` primitive — URL-backed links with
// `aria-current`, underlined in the accent. It used to be four bare
// hyperlinks in a flex row ("some tabs are just hyperlinks", the owner), which
// is exactly what gate check 3a ratchets away.
//
// RENDER-ONCE (§5), AND THE WHOLE AREA AT ONCE. The role feed decides more
// than the Cost-runs tab: it decides whether the page head carries a primary
// action on EVERY screen under here (New product, Add unit, New cost run, New
// template). Gating only the strip left both halves of that shift on the
// glass — measured on the replica at 412…1440: the crumbs dropped 5 px when
// the fourth tab resolved and another 4 px when the primary button appeared,
// CLS 0.0476 on the flagship list.
//
// So the AREA gates, and reveals ATOMICALLY — the §5 content rule the office
// home already follows: while the feed is outstanding the area is a reserving
// skeleton; when it settles the whole subtree REMOUNTS (`key` swap), so no
// element that was painted early ever moves. Nothing here waits on DATA — only
// on the one cached role query, which every later navigation reads warm.

import { Outlet } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { RouterTabs, type RouterTabDef } from "../../../components/tabs";
import { CardSkeleton } from "../../../components/states";
import { useOnboardingPermissions } from "../../onboarding/hooks/permissions";
import { CATALOGUE_IMPORT_KINDS } from "../../onboarding/screens/imports/import-model";
import { useCataloguePermissions } from "./hooks/permissions";
import "./catalogue.css";

export function CatalogueLayout() {
  const { t } = useTranslation();
  const perms = useCataloguePermissions();
  // The Import tab reads module 02's lanes off the same role feed; a reader
  // with no catalogue lane never sees it (spec 01 S6's roles).
  const onboarding = useOnboardingPermissions();
  const canImport = CATALOGUE_IMPORT_KINDS.some((kind) => onboarding.canImport(kind));

  // Cost runs are gated by catalogue.cost.read (§4 S7 "entire route guarded by
  // catalogue.cost.read"): a role without it never sees the nav entry (and the
  // route itself renders a not-available state if reached directly).
  const tabs: RouterTabDef[] = [
    {
      to: "/catalogue/products",
      label: t("catalogue.nav.products"),
      "data-testid": "cat-nav-products",
    },
    { to: "/catalogue/uoms", label: t("catalogue.nav.uoms"), "data-testid": "cat-nav-uoms" },
    ...(perms.has("catalogue.cost.read")
      ? [
          {
            to: "/catalogue/cost-runs",
            label: t("catalogue.nav.costRuns"),
            "data-testid": "cat-nav-cost-runs",
          },
        ]
      : []),
    {
      to: "/catalogue/templates",
      label: t("catalogue.nav.templates"),
      "data-testid": "cat-nav-templates",
    },
    ...(canImport
      ? [
          {
            to: "/catalogue/imports",
            label: t("catalogue.nav.imports"),
            "data-testid": "cat-nav-imports",
          },
        ]
      : []),
  ];

  if (!perms.isResolved || !onboarding.isResolved) {
    return (
      <div className="cat-area" key="cat-loading" data-testid="catalogue-loading">
        <div className="ds-tabs cat-subnav-skel" aria-hidden="true" />
        <div className="cat-area-body">
          <CardSkeleton lines={6} label={t("catalogue.state.loading")} />
        </div>
      </div>
    );
  }

  return (
    <div className="cat-area" key="cat-ready">
      <RouterTabs tabs={tabs} label={t("catalogue.nav.label")} data-testid="catalogue-subnav" />
      <div className="cat-area-body">
        <Outlet />
      </div>
    </div>
  );
}
